import { UseFormReturn } from "react-hook-form";

import { useDataProvider, useNotification } from "@refinedev/core";
import { create } from "zustand";

import { useEditCreated } from "@/components/common/enrichment-async-hint";
import { defineStepper } from "@/components/ui/stepper";
import { useGeodataRepository } from "@/pages/geodata/hooks/use-geodata-repository";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";
import {
  VenueAIEnrichmentStepType,
  VenueBasicType,
  VenueMediaType,
  createVenueFormSchema,
} from "@/pages/venues/schemas/venue.create.form";

export const { Stepper, useStepper } = defineStepper(
  { id: "basicInfo", title: "Basic Information", description: "Basic info" },
  {
    id: "aiEnrichment",
    title: "AI Enrichment",
    description: "Enrich venues using AI searching the web for information",
  },
  {
    id: "media",
    title: "Media & Shit",
    description: "Upload media, tags and other stuff",
  },
);

type State = {
  id?: string;
  basicInfoStep?: VenueBasicType;
  aiEnrichmentStep?: VenueAIEnrichmentStepType;
  mediaStep?: VenueMediaType;
  loading: Record<
    "basicInfo" | "aiEnrichment" | "media",
    "idle" | "success" | "loading" | "error"
  >;
};

type Action = {
  setId: (id: string) => void;
  updateSelectedPlace: (place: any, geodata: any) => void;
  updateBasicInfoStep: (basicInfo: VenueBasicType) => void;
  updateAIEnrichment: (params: VenueAIEnrichmentStepType) => void;
  updateMedia: (enrichment: VenueMediaType) => void;
  updateState: (state: Partial<State["loading"]>) => void;
};

export const useVenueCreateFormStore = create<State & Action>((set) => ({
  mediaStep: {
    id: "",
    hide: false,
    media: {
      photos: [],
      teasers: [],
    },
  },
  setId: (id) => set(() => ({ id })),
  updateSelectedPlace: (place: any, geodata: any) =>
    set(() => ({
      basicInfoStep: {
        name: place.displayName?.text,
        type: place.primaryTypeDisplayName?.text,
        address: place.formattedAddress ?? place.shortFormattedAddress,
        area: place.addressDescriptor.areas
          ?.filter((area: any) => ["NEAR", "WITHIN"].includes(area.containment))
          ?.map((area: any) => area.displayName?.text)
          ?.join(", "),
        geolocation: {
          latitude: place.location.latitude,
          longitude: place.location.longitude,
        },
        googleMapsId: place.id,
        timezone: place.timeZone.id,
        phoneNumber: place.internationalPhoneNumber,
        mapsUrl: place.googleMapsUri,
        geodata: geodata,
        place,
      },
    })),
  updateBasicInfoStep: (basicInfo) =>
    set(() => ({
      basicInfoStep: basicInfo,
    })),
  updateAIEnrichment: (params) => {
    set(() => ({
      aiEnrichmentStep: params,
    }));
  },
  updateMedia: (media) =>
    set(() => ({
      mediaStep: media,
    })),
  loading: {
    basicInfo: "idle",
    aiEnrichment: "idle",
    media: "idle",
  },
  updateState: (loading) =>
    set((state) => ({
      loading: { ...state.loading, ...loading },
    })),
}));

export const useVenueCreateForm = () => {
  const methods = useStepper();

  const { generateId, create, searchPlace } = useVenueRepository();
  const { search: searchGeodata } = useGeodataRepository();
  const {
    setId,
    updateBasicInfoStep,
    updateAIEnrichment,
    updateMedia,
    updateState,
    updateSelectedPlace,
    id,
    basicInfoStep,
    aiEnrichmentStep,
    mediaStep,
    loading,
  } = useVenueCreateFormStore();
  const { open } = useNotification();
  const editCreated = useEditCreated();

  const onPlaceSelected = async (place: any) => {
    const result = await searchGeodata(place.postalAddress.locality);
    updateSelectedPlace(place, result[0]);
  };

  const onBasicInfoStepSubmit = async (
    values: VenueBasicType,
    _: UseFormReturn<VenueBasicType, any, VenueBasicType>,
  ) => {
    console.debug("Basic Info Step Submitted", values);
    updateState({
      basicInfo: "loading",
    });
    const id = await generateId();
    setId(id);
    updateBasicInfoStep(values);
    console.debug("Generated id", id);

    updateState({
      basicInfo: "success",
    });

    methods.next();
  };

  const onAiEnrichmentStepSubmit = async (values: {
    instagramPageUrl: string;
    instagramScreenshot?: string;
  }) => {
    console.debug("AI Enrichment Step Submitted", values);
    updateAIEnrichment({
      instagramPageUrl: values.instagramPageUrl,
      instagramScreenshot: values.instagramScreenshot,
    });
    methods.next();
  };

  const onMediaStepSubmit = async (values: VenueMediaType) => {
    console.debug("Media Step Submitted", values);
    updateMedia(values);
    updateState({
      media: "loading",
    });
    const venueToCreate = createVenueFormSchema.safeParse({
      ...basicInfoStep,
      ...aiEnrichmentStep,
      ...values,
    });
    if (!venueToCreate.success) {
      const errorMessage = venueToCreate.error.errors
        .map((err) => `${err.path.join(".")}: ${err.message}`)
        .join("\n");
      open?.({
        type: "error",
        message: "Validation Error",
        description: errorMessage,
      });
      updateState({
        media: "error",
      });
      return;
    }

    await create(venueToCreate.data)
      .catch((error) => {
        open?.({
          type: "error",
          message: "Failed to create venue",
          description: error?.response?.data?.detail || error.message,
        });
        throw error;
      })
      .finally(() => {
        updateState({
          media: "success",
        });
      });
    open?.({
      type: "success",
      message: "Venue created successfully",
    });
    // `?created=1` — see `enrichment-async-hint.tsx`. A Tina-deferred create
    // returns before its enrichment job has run.
    editCreated("venues", id!);
  };

  return {
    methods,
    searchPlace,
    onPlaceSelected,
    onBasicInfoStepSubmit,
    onAiEnrichmentStepSubmit,
    onMediaStepSubmit,
    id,
    basicInfoStep,
    aiEnrichmentStep,
    mediaStep,
    updateMedia,
    loading,
  };
};
